import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";

/* ---------- NORMALIZER ---------- */
function normalizeVariety(v) {
  const res = v.resistance || {};

  function normRes(x) {
    if (!x) return "N/A";
    if (typeof x === "string") return x.toUpperCase();
    return "N/A";
  }

  return {
    name: v.name || "Unknown",
    market_class: v.market_class || v.marketClass || "—",
    release_year: v.release_year || v.year || "—",

    yield_kg_ha: Number(v.yield_kg_ha),
    maturity_days: Number(v.maturity_days),
    seed_weight_g: Number(v.seed_weight_g),
    harvestability: Number(v.harvestability),

    image: v.image
      ? v.image.startsWith("http")
        ? v.image
        : `/seed-catalog/images/${v.image}`
      : null,

    pdf: v.pdf
      ? v.pdf.startsWith("http")
        ? v.pdf
        : `/seed-catalog/pdfs/${v.pdf}`
      : null,

    "Common Mosaic Virus R1": normRes(v["Common Mosaic Virus R1"] || res.CMV_R1),
    "Common Mosaic Virus R15": normRes(v["Common Mosaic Virus R15"] || res.CMV_R15),
    "Anthracnose R17": normRes(v["Anthracnose R17"] || res.Anthracnose_R17),
    "Anthracnose R23": normRes(v["Anthracnose R23"] || res.Anthracnose_R23),
    "Anthracnose R73": normRes(v["Anthracnose R73"] || res.Anthracnose_R73),
    "Common Blight": normRes(v["Common Blight"] || res.CommonBlight),
  };
}

/* ---------- HEATMAP ---------- */
function HeatmapGrid({ data = [], xLabels = [], yLabels = [] }) {
  if (!data.length) return <div>No resistance data available</div>;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 220, fontWeight: 700 }}>Variety</div>
        {xLabels.map((d, i) => (
          <div key={i} style={{ width: 160, textAlign: "center", fontSize: 12 }}>
            {d}
          </div>
        ))}
      </div>

      {data.map((row, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <div style={{ width: 220 }}>{yLabels[i]}</div>
          {row.map((val, j) => {
            const bg =
              val === "R" ? "#2ecc71" :
              val === "S" ? "#e74c3c" :
              "#bdc3c7";
            return (
              <div key={j}
                style={{
                  width: 160,
                  height: 28,
                  borderRadius: 6,
                  background: bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}>
                {val}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ---------- MAIN ---------- */
export default function SeedCatalog() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [marketFilter, setMarketFilter] = useState("All");
  const [barTrait, setBarTrait] = useState("yield_kg_ha");
  const [activeVariety, setActiveVariety] = useState(null);
  const [pdfVariety, setPdfVariety] = useState(null);

  const [radarTraits, setRadarTraits] = useState([
    "Yield","Earliness","Seed","Harvest"
  ]);

  useEffect(() => {
    fetch("/seed-catalog/data/uog-varieties.json")
      .then(r => r.json())
      .then(json => setData(json.map(normalizeVariety)))
      .catch(err => {
        console.error("JSON load failed", err);
        setData([]);
      });
  }, []);

  useEffect(() => {
    setSelected([]);
  }, [marketFilter]);

  const marketClasses = useMemo(() => {
    if (!Array.isArray(data)) return ["All"];
    return ["All", ...new Set(data.map(d => d.market_class))];
  }, [data]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return marketFilter === "All"
      ? data
      : data.filter(d => d.market_class === marketFilter);
  }, [data, marketFilter]);

  const selectedRows = useMemo(() => {
    return filteredData.filter(v => selected.includes(v.name));
  }, [filteredData, selected]);

  function toggleSelect(name) {
    setSelected(s =>
      s.includes(name)
        ? s.filter(x => x !== name)
        : [...s, name]
    );
  }

  const diseases = [
    "Common Mosaic Virus R1",
    "Common Mosaic Virus R15",
    "Anthracnose R17",
    "Anthracnose R23",
    "Anthracnose R73",
    "Common Blight",
  ];

  const heatData = useMemo(() => {
    return selectedRows.map(v =>
      diseases.map(d => v[d])
    );
  }, [selectedRows]);

  const barData = useMemo(() => {
    return selectedRows.map(v => ({
      name: v.name,
      value: v[barTrait],
    }));
  }, [selectedRows, barTrait]);

  /* ---------- RADAR DATA ---------- */
  const numericRanges = useMemo(() => {
    if (!data?.length) return {};
    return {
      yieldMin: Math.min(...data.map(d => d.yield_kg_ha || 0)),
      yieldMax: Math.max(...data.map(d => d.yield_kg_ha || 1)),
      maturityMin: Math.min(...data.map(d => d.maturity_days || 0)),
      maturityMax: Math.max(...data.map(d => d.maturity_days || 1)),
      seedMin: Math.min(...data.map(d => d.seed_weight_g || 0)),
      seedMax: Math.max(...data.map(d => d.seed_weight_g || 1)),
    };
  }, [data]);

  function scale100(val, min, max) {
    if (val == null || max === min) return 0;
    return ((val - min) / (max - min)) * 100;
  }

  const radarData = useMemo(() => {
    return radarTraits.map(axis => {
      const obj = { axis };
      selectedRows.forEach(v => {
        let val = 0;
        if (axis === "Yield") {
          val = scale100(v.yield_kg_ha, numericRanges.yieldMin, numericRanges.yieldMax);
        } else if (axis === "Earliness") {
          val = scale100(
            numericRanges.maturityMax - v.maturity_days,
            0,
            numericRanges.maturityMax - numericRanges.maturityMin
          );
        } else if (axis === "Seed") {
          val = scale100(v.seed_weight_g, numericRanges.seedMin, numericRanges.seedMax);
        } else if (axis === "Harvest") {
          val = scale100(v.harvestability, 1, 5);
        }
        obj[v.name] = Math.round(val);
      });
      return obj;
    });
  }, [selectedRows, radarTraits, numericRanges]);

  const colors = ["#3498db","#e74c3c","#2ecc71","#9b59b6","#f39c12","#1abc9c"];

  if (data === null) return <div style={{ padding: 30 }}>Loading…</div>;

  return (
    <div style={{ padding: 20 }}>
      {/* HEADER with logo on the top-right */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20
      }}>
        <h1 style={{ margin: 0 }}>UoG Variety Analytics Dashboard</h1>
        <img
          src="/seed-catalog/logo.png"
          alt="UoG Logo"
          style={{ height: 60 }}
        />
      </div>

      Market:
      <select value={marketFilter} onChange={e => setMarketFilter(e.target.value)}>
        {marketClasses.map(m => <option key={m}>{m}</option>)}
      </select>

      <div style={{ marginTop: 10 }}>
        {filteredData.map(v => (
          <label key={v.name} style={{ marginRight: 10 }}>
            <input
              type="checkbox"
              checked={selected.includes(v.name)}
              onChange={() => toggleSelect(v.name)}
            />
            {v.name}
          </label>
        ))}
      </div>

      {/* TABLE */}
      {selectedRows.length > 0 && (
        <>
          <h2>Full Variety Table</h2>
          <table border="1" cellPadding="6">
            <thead>
              <tr>
                <th>Name</th>
                <th>Market</th>
                <th>Release</th>
                <th>Yield</th>
                <th>Maturity</th>
                <th>Seed</th>
                <th>Harvestability</th>
                {diseases.map(d => <th key={d}>{d}</th>)}
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {selectedRows.map(v => (
                <tr key={v.name}>
                  <td style={{color:"#1a73e8",cursor:"pointer"}} onClick={() => setActiveVariety(v)}>
                    {v.name}
                  </td>
                  <td>{v.market_class}</td>
                  <td>{v.release_year}</td>
                  <td>{v.yield_kg_ha}</td>
                  <td>{v.maturity_days}</td>
                  <td>{v.seed_weight_g}</td>
                  <td>{v.harvestability}</td>
                  {diseases.map(d => <td key={d}>{v[d]}</td>)}
                  <td>
                    {v.pdf 
                      ? <span style={{color:"#1a73e8",cursor:"pointer"}} onClick={() => window.open(v.pdf)}>PDF</span>
                      : <span style={{color:"#999",cursor:"pointer"}} onClick={() => setPdfVariety(v)}>No PDF</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* HEATMAP */}
      <h2>Resistance Heatmap</h2>
      <HeatmapGrid data={heatData} xLabels={diseases} yLabels={selectedRows.map(v => v.name)} />

      {/* BAR */}
      <h2>Trait Comparison</h2>
      Trait:
      <select value={barTrait} onChange={e => setBarTrait(e.target.value)}>
        <option value="yield_kg_ha">Yield</option>
        <option value="maturity_days">Maturity</option>
        <option value="seed_weight_g">Seed weight</option>
        <option value="harvestability">Harvestability</option>
      </select>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={barData}>
          <CartesianGrid />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value">
            {barData.map((_, i) => (
              <Cell key={i} fill={colors[i % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* RADAR */}
      <h2>Ideotype Radar</h2>
      {["Yield","Earliness","Seed","Harvest"].map(t => (
        <label key={t} style={{marginRight:10}}>
          <input
            type="checkbox"
            checked={radarTraits.includes(t)}
            onChange={() =>
              setRadarTraits(r =>
                r.includes(t) ? r.filter(x => x !== t) : [...r, t]
              )
            }
          />
          {t}
        </label>
      ))}

      <ResponsiveContainer width="100%" height={420}>
        <RadarChart data={radarData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="axis" />
          <PolarRadiusAxis domain={[0,100]} />
          {selectedRows.map((v,i) => (
            <Radar key={v.name} name={v.name} dataKey={v.name}
              stroke={colors[i%colors.length]} fillOpacity={0.25}/>
          ))}
          <Legend />
        </RadarChart>
      </ResponsiveContainer>

      {/* IMAGE MODAL */}
      {activeVariety && (
        <div onClick={()=>setActiveVariety(null)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
          display:"flex",alignItems:"center",justifyContent:"center"
        }}>
          {activeVariety.image
            ? <img src={activeVariety.image} style={{maxWidth:"85%",maxHeight:"85%"}} alt={activeVariety.name}/>
            : <div style={{background:"white",padding:40,borderRadius:12}}>
                Image for <b>{activeVariety.name}</b> will be uploaded soon.
              </div>}
        </div>
      )}

      {/* PDF MODAL */}
      {pdfVariety && (
        <div onClick={()=>setPdfVariety(null)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
          display:"flex",alignItems:"center",justifyContent:"center"
        }}>
          <div style={{background:"white",padding:40,borderRadius:12}}>
            PDF for <b>{pdfVariety.name}</b> is not available yet.
          </div>
        </div>
      )}
    </div>
  );
}